import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  TextField,
  IconButton,
  Grid,
  Card,
  CardContent,
  CardHeader,
  Divider,
  Button,
  Stack,
  Chip,
  Collapse,
} from '@mui/material';
import Title from '../../components/Title';
import EditModal from '../../components/EditModal';
import { useAccount } from '../../context/AccountContext';
import { useNotification } from '../../context/NotificationContext';
import { API_ERROR_MESSAGE, NA_LABEL } from '../../common/AppConstants';
import { AccountCircle, Home } from '@mui/icons-material';
import EditTwoToneIcon from '@mui/icons-material/EditTwoTone';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PersonOutlinedIcon from '@mui/icons-material/PersonOutlined';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import WorkspacePremiumOutlinedIcon from '@mui/icons-material/WorkspacePremiumOutlined';
import ManageAccountsOutlinedIcon from '@mui/icons-material/ManageAccountsOutlined';
import { MANAGE_SUBSCRIPTION_URL } from '../../common/AppUrls';
import { httpRequest } from '../../api/helper';
import { USER_ROLES } from '../../common/AppConstants';
import { humanReadableDate, formatCurrency, titleCase } from '../../common/AppUtils';
import LogoUploader from '../../components/LogoUploader';

const TITLE = 'Account';
const ORG_API_URL = 'organization/organization/';
const ACTIVE_SUBSCRIPTION_API = "billing/active-subscription/";

const ORG_SETUP_API_URL = 'organization/org-type/';

const orgSetupColumns = [
  {
    "attr": "org_type",
    "label": "Organization type",
    "type": "choice",
    "related": true,
    "required": true,
    "editable": true,
    "extra": false,
    "help_text": "Organization type"
  },
];

const orgSetupRelatedData = {
  org_type: {
    education: {
      label: "Education/School"
    },
    fitness: {
      label: "Fitness/Yoga/Gym"
    }
  }
};

const orgColumns = [
  {
    "attr": "name",
    "label": "Name",
    "type": "text",
    "related": false,
    "required": true,
    "editable": true,
    "extra": false,
    "help_text": "Name of the organization"
  },
  {
    "attr": "address",
    "label": "Address",
    "type": "text",
    "related": false,
    "required": true,
    "editable": true,
    "extra": false,
    "help_text": "Address"
  },
  {
    "attr": "country",
    "label": "Country",
    "type": "choice",
    "related": true,
    "required": true,
    "editable": true,
    "extra": false,
    "help_text": "Country"
  },
  {
    "attr": "place_of_supply",
    "label": "State",
    "type": "choice",
    "related": true,
    "required": true,
    "editable": true,
    "extra": false,
    "help_text": "State"
  },
  {
    "attr": "gstin",
    "label": "GSTIN",
    "type": "text",
    "related": false,
    "required": false,
    "editable": true,
    "extra": false,
    "help_text": "GSTIN of Institute"
  },
  // {
  //   "attr": "brand_label",
  //   "label": "Brand Label",
  //   "type": "text",
  //   "related": false,
  //   "required": true,
  //   "editable": true,
  //   "extra": false,
  //   "help_text": "Brand - primary text"
  // },
  // {
  //   "attr": "brand_tagline",
  //   "label": "Brand Tagline",
  //   "type": "text",
  //   "related": false,
  //   "required": true,
  //   "editable": true,
  //   "extra": false,
  //   "help_text": "Brand - secondary text"
  // },
];

function SubscriptionCard({ isFreeTrial, subscriptionData }) {
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState(false);
  const [detailsExpanded, setDetailsExpanded] = useState(false);

  const payment = subscriptionData?.payments?.[0];

  return (
    <Card
      variant="outlined"
      sx={{
        maxWidth: 'md',
        display: 'flex',
        flexDirection: 'column',
        borderRadius: 2,
        transition: 'box-shadow 0.3s',
        '&:hover': {
          boxShadow: 3
        },
        mb: 2,
      }}
    >
      <CardHeader
        title={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="h4">Subscription</Typography>
          </Box>
        }
        sx={{ pb: 2 }}
      />
      <Divider />

      <CardContent sx={{ pt: 0, pb: 1, flexGrow: 1, display: 'flex' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, pt: 2, width: '100%' }}>
          {isFreeTrial ? (
            <Typography variant="body2">
              <Typography component="span" variant="body2" fontWeight="bold">Current Plan:</Typography>
              <Button
                variant="contained"
                color="primary"
                size="small"
                sx={{
                  ml: 1,
                  height: '20px',
                }}
              >
                Free Trial
              </Button>
            </Typography>
          ) : (
            <>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
                <Typography fontWeight={600} color="primary.main">{subscriptionData.plan_name}</Typography>
                <Chip
                  size="small"
                  label={titleCase(subscriptionData.billing_cycle)}
                  variant="outlined"
                  sx={{
                    maxHeight: 18,
                    minWidth: 36,
                    fontSize: '0.8rem',
                  }}
                />
              </Box>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
                <Typography variant="body2" color="text.secondary" mb={0.5}>
                  {humanReadableDate(subscriptionData.start_date)}
                  {' → '}
                  {humanReadableDate(subscriptionData.end_date)}
                </Typography>
                <IconButton
                  size="small"
                  onClick={() => setDetailsExpanded((v) => !v)}
                  sx={{ p: 0.25 }}
                >
                  {detailsExpanded ? (
                    <ExpandLessIcon fontSize="small" />
                  ) : (
                    <ExpandMoreIcon fontSize="small" />
                  )}
                </IconButton>
              </Box>
          
              {/* Details toggle: limits, modules, payments */}
              <Collapse in={detailsExpanded}>
                <Divider sx={{ my: 1 }} />
                <Box sx={{ pt: 1, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  <Stack direction="row" spacing={3}>
                    {subscriptionData?.staff_limit > 0 && (
                      <Stack direction="row" spacing={1} alignItems="center">
                        <PersonOutlinedIcon fontSize="small" color="action" />
                        <Typography variant="body2">
                          {subscriptionData.staff_limit} Teachers
                        </Typography>
                      </Stack>
                    )}

                    {subscriptionData?.student_limit > 0 && (
                      <Stack direction="row" spacing={1} alignItems="center">
                        <GroupsOutlinedIcon fontSize="small" color="action" />
                        <Typography variant="body2">
                          {subscriptionData.student_limit} Students
                        </Typography>
                      </Stack>
                    )}

                    {subscriptionData?.member_limit > 0 && (
                      <Stack direction="row" spacing={1} alignItems="center">
                        <GroupsOutlinedIcon fontSize="small" color="action" />
                        <Typography variant="body2">
                          {subscriptionData.member_limit} Members
                        </Typography>
                      </Stack>
                    )}
                  </Stack>

                  {subscriptionData?.modules?.length > 0 && (
                    <Box>
                      <Typography fontWeight={700} color="primary.main" gutterBottom>
                        Modules
                      </Typography>
                      <Stack spacing={1}>
                        {subscriptionData.modules.map((m) => (
                          <Stack key={m.code} direction="row" alignItems="center" spacing={1}>
                            <CheckCircleIcon color="success" fontSize="small" />
                            <Typography variant="body2">{m.name}</Typography>
                          </Stack>
                        ))}
                      </Stack>
                    </Box>
                  )}

                  {payment && (
                    <Box
                      sx={{
                        mb: 1,
                        p: 1.25,
                        bgcolor: 'grey.50',
                        borderRadius: 1,
                      }}
                    >
                      <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                        <Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography fontWeight={600} color="success.main">
                              {formatCurrency(payment.amount)}
                            </Typography>
                          </Box>

                          <Typography variant="body2">
                            {humanReadableDate(payment.invoice_date)}
                          </Typography>
                        </Box>

                        <Box
                          sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'flex-end',
                            gap: 1.25,
                          }}
                        >
                          <Chip
                            size="small"
                            label={titleCase(payment.status)}
                            color={
                              payment.status === 'paid' ? 'success' :
                              payment.status === 'pending' ? 'warning' :
                              'error'
                            }
                            sx={{ height: 20, fontSize: 11 }}
                          />
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              setExpanded((v) => !v);
                            }}
                            sx={{ p: 0.25 }}
                          >
                            {expanded ? (
                              <ExpandLessIcon fontSize="small" />
                            ) : (
                              <ExpandMoreIcon fontSize="small" />
                            )}
                          </IconButton>
                        </Box>
                      </Box>

                      <Collapse in={expanded}>
                        <Divider sx={{ my: 1 }} />
                        {payment.line_items.map((item) => (
                          <Box key={item.id} display="flex" justifyContent="space-between" sx={{ py: 0.25 }}>
                            <Typography
                              variant="body2"
                              noWrap
                              sx={{ maxWidth: 240 }}
                            >
                              {item.description}
                            </Typography>
                            <Box display="flex" alignItems="center" gap={1}>
                              {Number(item.discount) > 0 && (
                                <Typography
                                  variant="caption"
                                  sx={{ color: 'text.disabled', textDecoration: 'line-through' }}
                                >
                                  {formatCurrency(item.unit_price)}
                                </Typography>
                              )}
                              <Typography variant="body2">{formatCurrency(item.amount)}</Typography>
                            </Box>
                          </Box>
                        ))}
                        <Box display="flex" justifyContent="space-between" sx={{ py: 0.25 }}>
                          <Typography variant="body2">
                            Tax
                          </Typography>
                          <Box display="flex" alignItems="center" gap={1}>
                            <Typography variant="body2">{formatCurrency(payment.tax_amount)}</Typography>
                          </Box>
                        </Box>
                        <Box display="flex" justifyContent="space-between" sx={{ py: 0.25 }}>
                          <Typography variant="body2">
                            Total
                          </Typography>
                          <Box display="flex" alignItems="center" gap={1}>
                            <Typography variant="body2">{formatCurrency(payment.amount)}</Typography>
                          </Box>
                        </Box>
                      </Collapse>
                    </Box>
                  )}
                </Box>
              </Collapse>
            </>
          )}

          <Button
            onClick={() => navigate(MANAGE_SUBSCRIPTION_URL)}
            variant="contained"
            color={isFreeTrial ? 'success' : 'primary'}
            sx={{ mt: 2 }}
            startIcon={isFreeTrial ? <WorkspacePremiumOutlinedIcon /> : <ManageAccountsOutlinedIcon />}
          >
            {isFreeTrial ? 'Upgrade Now' : 'Manage Subscription'}
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
}

export default function Account() {
  const { addNotification } = useNotification();
  const { account } = useAccount();

  const [isOrgEditModalOpen, setIsOrgEditModalOpen] = useState(false);

  const [relatedData, setRelatedData] = useState({});
  const [orgData, setOrgData] = useState({});
  const [isOrgDataLoading, setIsOrgDataLoading] = useState(true);
  const [subscriptionData, setSubscriptionData] = useState({});

  const isFreeTrial = subscriptionData?.code === 'free_trial';

  const isOrgSetupModalOpen =
    !!account?.organization?.id &&
    !account?.organization?.org_type;

  const handleOrgSetupModalClose = () => {
    window.location.reload();
  };

  const handleOrgSetupSave = async (updatedEditRow, onEditFailure) => {
    const finalFormData = {
      ...updatedEditRow,
      id: account?.organization?.id,
    };
    await httpRequest({
      method: 'PUT',
      url: ORG_SETUP_API_URL,
      data: finalFormData,
      onSuccess: (data) => {
        addNotification('Updated successfully!', 'info');
        handleOrgSetupModalClose();
      },
      onFailure: (data) => {
        onEditFailure(data);
      },
      addNotification,
    });
  };

  const handleOrgEditModalClose = () => {
    setIsOrgEditModalOpen(false);
  };

  const handleOrgEditSave = async (updatedEditRow, onEditFailure) => {
    await httpRequest({
      method: 'PUT',
      url: ORG_API_URL,
      data: updatedEditRow,
      onSuccess: (data) => {
        handleOrgEditModalClose();
        // await fetchAccountData();
        setOrgData(data);
        addNotification('Updated successfully!', 'info');
      },
      onFailure: (data) => {
        onEditFailure(data);
      },
      addNotification,
    });
  };

  useEffect(() => {
    const fetchData = async () => {
      fetchOrgData();
      fetchSubscriptionData();
    };
    if (account?.user?.current_role?.toLowerCase() !== USER_ROLES.ADMIN) {
      return;
    }
    fetchData();
  }, [account]);

  useEffect(() => {
    // Provide mandatroy Institute Data
    if (isOrgDataLoading) {
      return;
    }
    if (account?.user?.current_role?.toLowerCase() !== USER_ROLES.ADMIN) {
      return;
    }

    if (!orgData.country) {
      console.log("Please provide country");
      addNotification("Please provide country", "info");
      setIsOrgEditModalOpen(true);
    }
    else if (orgData.country === "IN" && !orgData.place_of_supply) {
      console.log("Please provide state and gstin(optional)");
      addNotification("Please provide state and gstin(optional)", "info");
      setIsOrgEditModalOpen(true);
    }    
  }, [account, isOrgDataLoading, orgData]);

  const fetchOrgData = async (event) => {
    setIsOrgDataLoading(true);
    await httpRequest({
      method: 'GET',
      url: ORG_API_URL,
      onSuccess: (data) => {
        setRelatedData(data.relatedData);
        setOrgData(data.organization);
      },
      addNotification,
    });
    setIsOrgDataLoading(false);
  };
  
  const fetchSubscriptionData = async (event) => {
    await httpRequest({
      method: 'GET',
      url: ACTIVE_SUBSCRIPTION_API,
      onSuccess: (data) => {
        setSubscriptionData(data);
      },
      addNotification,
    });
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: 'calc(100vh - 64px - 64px)', // viewport height - margings - app bar
      }}
    >
      {/* Title and Button (fixed) */}
      <Box
        sx={{
          flexShrink: 0, // Top level space distribution
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Title */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            mt: 1,
            mb: 1,
          }}
        >
          <Title>{TITLE}</Title>
        </Box>
      </Box>

      <Box
        sx={{
          flexGrow: 1, // This makes the table container grow and take the available space
          overflowY: 'auto', // Only the table will scroll
          mt: 2,
        }}
      >
        {/* Organization Profile Section */}
        <Card
          variant="outlined"
          sx={{
            maxWidth: 'md',
            display: 'flex',
            flexDirection: 'column',
            borderRadius: 2,
            transition: 'box-shadow 0.3s',
            '&:hover': {
              boxShadow: 3
            },
            mb: 2,
          }
        }>
          <CardHeader
            title={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="h4">Institute Information</Typography>
                {/* More data chips if needed */}
              </Box>
            }
            action={
              <IconButton onClick={() => setIsOrgEditModalOpen(true)} size="small">
                <EditTwoToneIcon fontSize="small" />
              </IconButton>
            }
            sx={{ pb: 2 }}
          />
          <Divider/>
          
          <CardContent sx={{ pt: 0, pb: 1, flexGrow: 1, display: 'flex' }}>
            <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', gap: 1, pt: 2 }}>
              <Typography variant="body2">
                <Typography component="span" variant="body2" fontWeight="bold">Name:</Typography> {orgData.name ?? NA_LABEL}
              </Typography>
              <Typography variant="body2">
                <Typography component="span" variant="body2" fontWeight="bold">Institute Id:</Typography> {orgData.id ?? NA_LABEL}
              </Typography>
              <Typography variant="body2">
                <Typography component="span" variant="body2" fontWeight="bold">Address:</Typography> {orgData.address ?? NA_LABEL}
              </Typography>
              <Typography variant="body2">
                <Typography component="span" variant="body2" fontWeight="bold">Country:</Typography> {relatedData?.country?.[orgData.country]?.label ?? NA_LABEL}
              </Typography>
              <Typography variant="body2">
                <Typography component="span" variant="body2" fontWeight="bold">State:</Typography> {relatedData?.place_of_supply?.[orgData.place_of_supply]?.name ?? NA_LABEL}
              </Typography>
              <Typography variant="body2">
                <Typography component="span" variant="body2" fontWeight="bold">GSTIN:</Typography> {orgData.gstin ?? NA_LABEL}
              </Typography>
            </Box>
          </CardContent>
        </Card>

        {/* Organization Plan Subscription Section */}
        { account?.user?.current_role?.toLowerCase() === USER_ROLES.ADMIN && (
          <SubscriptionCard isFreeTrial={isFreeTrial} subscriptionData={subscriptionData} />
        )}

        {/* Organization Branding Section */}
        {/* <Card
          variant="outlined"
          sx={{
            maxWidth: 'md',
            display: 'flex',
            flexDirection: 'column',
            borderRadius: 2,
            transition: 'box-shadow 0.3s',
            '&:hover': {
              boxShadow: 3
            },
            mb: 2,
          }
        }>
          <CardHeader
            title={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="h4">Institute Branding</Typography>
              </Box>
            }
            action={
              <IconButton onClick={() => setIsOrgEditModalOpen(true)} size="small">
                <EditTwoToneIcon fontSize="small" />
              </IconButton>
            }
            sx={{ pb: 2 }}
          />
          <Divider/>
          
          <CardContent sx={{ pt: 0, pb: 1, flexGrow: 1, display: 'flex', }}>
            <LogoUploader/>

            <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', gap: 1, pt: 2 }}>
              <Typography variant="body2">
                <Typography component="span" variant="body2" fontWeight="bold">Brand Text:</Typography> {orgData.brand_label ?? NA_LABEL}
              </Typography>
              <Typography variant="body2">
                <Typography component="span" variant="body2" fontWeight="bold">Brand Tagline:</Typography> {orgData.brand_tagline ?? NA_LABEL}
              </Typography>
            </Box>
          </CardContent>
        </Card> */}

        {/* Org Setup org-type Edit Modal */}
        <Box>
          <EditModal
            open={isOrgSetupModalOpen}
            columns={orgSetupColumns}
            rowData={{}}
            relatedData={orgSetupRelatedData}
            handleClose={handleOrgSetupModalClose}
            handleSave={handleOrgSetupSave}
            edit_title="Update Organization"
          />
        </Box>

        {/* Org Edit Modal */}
        <Box>
          <EditModal
            open={isOrgEditModalOpen}
            columns={orgColumns}
            rowData={orgData}
            relatedData={relatedData}
            handleClose={handleOrgEditModalClose}
            handleSave={handleOrgEditSave}
            edit_title="Edit Institute"
          />
        </Box>
      </Box>
    </Box>
  );
}
